import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UserAvatar } from '@/components/UserAvatar';
import SharedExpenses from '@/components/SharedExpenses';
import { api } from '@/lib/api';
import { BUDGET, UPGRADE } from '@/constants/testIds';
import { Wallet, Lock, Sparkles, AlertTriangle, Check, Pencil, RefreshCw, Lightbulb } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORY_META = [
    { key: 'accommodation', label: 'Stay', testid: BUDGET.accommodationInput },
    { key: 'food', label: 'Food', testid: BUDGET.foodInput },
    { key: 'transport', label: 'Transport', testid: BUDGET.transportInput },
    { key: 'activities', label: 'Activities', testid: BUDGET.activitiesInput },
];

function fmtMoney(amount, currency) {
    if (amount === null || amount === undefined || amount === '') return '—';
    try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(Number(amount));
    } catch {
        return `${currency} ${Number(amount).toLocaleString()}`;
    }
}

function StatusChip({ status }) {
    const map = {
        aligned: { bg: 'rgba(63,142,91,0.12)', color: 'var(--ns-success,#3F8E5B)', label: 'Aligned' },
        watch: { bg: 'rgba(224,122,95,0.12)', color: 'var(--ns-clay)', label: 'Some variance' },
        diverging: { bg: 'rgba(199,68,68,0.12)', color: '#C74444', label: 'Diverging' },
        insufficient: { bg: 'rgba(0,0,0,0.05)', color: 'var(--ns-text3)', label: 'Need more inputs' },
    };
    const m = map[status] || map.insufficient;
    return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium" style={{ background: m.bg, color: m.color }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: m.color }} /> {m.label}
        </span>
    );
}

function PersonalBudgetForm({ initial, currency, onSave, onCancel, submitting }) {
    const [total, setTotal] = useState(initial?.total_ceiling ?? '');
    const [cats, setCats] = useState({
        accommodation: initial?.categories?.accommodation ?? '',
        food: initial?.categories?.food ?? '',
        transport: initial?.categories?.transport ?? '',
        activities: initial?.categories?.activities ?? '',
    });
    const [notes, setNotes] = useState(initial?.notes ?? '');

    const submit = (e) => {
        e?.preventDefault?.();
        const num = (v) => (v === '' || v === null ? null : Number(v));
        onSave({
            total_ceiling: num(total),
            categories: {
                accommodation: num(cats.accommodation),
                food: num(cats.food),
                transport: num(cats.transport),
                activities: num(cats.activities),
            },
            notes,
        });
    };

    return (
        <form data-testid={BUDGET.personalForm} onSubmit={submit} className="ns-card p-6 sm:p-8 space-y-5">
            <div>
                <div className="text-xs uppercase tracking-[0.18em] text-[var(--ns-text3)] mb-2">Your overall ceiling</div>
                <div className="flex items-center gap-2">
                    <span className="text-sm text-[var(--ns-text2)] tabular-nums">{currency}</span>
                    <Input
                        type="number"
                        min="0"
                        step="1"
                        value={total}
                        onChange={(e) => setTotal(e.target.value)}
                        placeholder="e.g. 1500"
                        data-testid={BUDGET.totalInput}
                        className="rounded-xl border-[var(--ns-border)] bg-white"
                    />
                </div>
                <p className="text-xs text-[var(--ns-text3)] mt-1.5">Total you're comfortable spending across the trip.</p>
            </div>

            <div>
                <div className="text-xs uppercase tracking-[0.18em] text-[var(--ns-text3)] mb-2">Per-category split (optional)</div>
                <div className="grid grid-cols-2 gap-3">
                    {CATEGORY_META.map((c) => (
                        <label key={c.key} className="block">
                            <span className="text-xs text-[var(--ns-text2)] block mb-1">{c.label}</span>
                            <Input
                                type="number"
                                min="0"
                                step="1"
                                value={cats[c.key]}
                                onChange={(e) => setCats({ ...cats, [c.key]: e.target.value })}
                                placeholder="0"
                                data-testid={c.testid}
                                className="rounded-xl border-[var(--ns-border)] bg-white"
                            />
                        </label>
                    ))}
                </div>
            </div>

            <div>
                <div className="text-xs uppercase tracking-[0.18em] text-[var(--ns-text3)] mb-2">Notes for the group (optional)</div>
                <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="e.g. Happy to splurge on food, but want to keep stay basic."
                    data-testid={BUDGET.notesInput}
                    className="rounded-2xl border-[var(--ns-border)] bg-white min-h-[80px]"
                />
            </div>

            <div className="flex items-center gap-3 pt-2">
                <Button
                    type="submit"
                    disabled={submitting}
                    data-testid={BUDGET.submitButton}
                    className="rounded-full h-11 px-6 text-white"
                    style={{ background: 'var(--ns-navy)' }}
                >
                    {submitting ? 'Saving…' : 'Save my budget'}
                </Button>
                {onCancel && (
                    <Button type="button" variant="ghost" onClick={onCancel} className="rounded-full">Cancel</Button>
                )}
            </div>
        </form>
    );
}

function PremiumLock({ title, description, onUpgrade, testid }) {
    return (
        <div data-testid={testid} className="ns-card p-6 sm:p-8 border-dashed">
            <div className="flex items-start gap-4">
                <span className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: 'var(--ns-soft)' }}>
                    <Lock size={16} className="text-[var(--ns-clay)]" />
                </span>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-display text-lg font-semibold tracking-tight">{title}</h4>
                        <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full" style={{ background: 'var(--ns-clay)', color: 'white' }}>Premium</span>
                    </div>
                    <p className="text-sm text-[var(--ns-text2)] mt-1 leading-relaxed">{description}</p>
                    <Button
                        onClick={onUpgrade}
                        data-testid={BUDGET.upgradeCta}
                        className="mt-4 rounded-full h-10 px-5 text-white"
                        style={{ background: 'var(--ns-navy)' }}
                    >
                        <Sparkles size={14} className="mr-2" /> Upgrade to unlock
                    </Button>
                </div>
            </div>
        </div>
    );
}

function CostBreakdownCard({ breakdown, currency }) {
    if (!breakdown) return null;
    const cats = breakdown.per_person_per_day || {};
    return (
        <div data-testid={BUDGET.breakdownCard} className="ns-card p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-1">
                <span className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(29,45,68,0.08)' }}>
                    <Lightbulb size={16} className="text-[var(--ns-navy)]" />
                </span>
                <div>
                    <div className="font-display text-lg font-semibold tracking-tight">AI cost breakdown</div>
                    <div className="text-xs text-[var(--ns-text3)]">Per-person per-day in {currency} · {breakdown.destination}</div>
                </div>
            </div>
            <div className="overflow-x-auto mt-4">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-xs uppercase tracking-widest text-[var(--ns-text3)]">
                            <th className="text-left font-medium py-2">Category</th>
                            <th className="text-right font-medium py-2 px-2">Budget</th>
                            <th className="text-right font-medium py-2 px-2">Mid</th>
                            <th className="text-right font-medium py-2 px-2">Luxury</th>
                        </tr>
                    </thead>
                    <tbody>
                        {CATEGORY_META.map((c) => {
                            const row = cats[c.key] || {};
                            return (
                                <tr key={c.key} className="border-t border-[var(--ns-border)]">
                                    <td className="py-3">
                                        <div className="font-medium text-[var(--ns-text)]">{c.label}</div>
                                        {row.note && <div className="text-xs text-[var(--ns-text3)] mt-0.5">{row.note}</div>}
                                    </td>
                                    <td className="text-right tabular-nums py-3 px-2">{fmtMoney(row.budget, currency)}</td>
                                    <td className="text-right tabular-nums py-3 px-2 font-medium">{fmtMoney(row.mid, currency)}</td>
                                    <td className="text-right tabular-nums py-3 px-2 text-[var(--ns-text3)]">{fmtMoney(row.luxury, currency)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {breakdown.tips?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-[var(--ns-border)] space-y-1.5">
                    {breakdown.tips.map((t, i) => (
                        <div key={i} className="text-xs text-[var(--ns-text2)] flex items-start gap-2">
                            <span style={{ color: 'var(--ns-clay)' }}>·</span> {t}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function SharedEstimateCard({ shared, currency }) {
    if (!shared) return null;
    return (
        <div data-testid={BUDGET.sharedEstimateCard} className="ns-card p-6 sm:p-8">
            <div className="font-display text-lg font-semibold tracking-tight">Shared cost estimator</div>
            <div className="text-xs text-[var(--ns-text3)] mt-0.5">{shared.days} days · {shared.participants} traveler{shared.participants === 1 ? '' : 's'}</div>
            <div className="grid grid-cols-3 gap-3 mt-4">
                {['budget', 'mid', 'luxury'].map((tier) => (
                    <div key={tier} className="rounded-2xl border border-[var(--ns-border)] p-4 text-center">
                        <div className="text-xs uppercase tracking-[0.18em] text-[var(--ns-text3)]">{tier === 'mid' ? 'Mid-range' : tier.charAt(0).toUpperCase() + tier.slice(1)}</div>
                        <div className="font-display text-xl font-semibold mt-1 tabular-nums">{fmtMoney(shared.per_person_total[tier], currency)}</div>
                        <div className="text-[10px] uppercase tracking-widest text-[var(--ns-text3)] mt-1">per person</div>
                        <div className="text-xs text-[var(--ns-text2)] mt-2 tabular-nums">Group: {fmtMoney(shared.group_total[tier], currency)}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default function BudgetTab({ trip, refreshKey, isOwner, onUpgrade, onTripUpdated }) {
    const tripId = trip.trip_id;
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [editing, setEditing] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [savingCurrency, setSavingCurrency] = useState(false);
    const [selectedCurrency, setSelectedCurrency] = useState(trip.currency || 'USD');

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/trips/${tripId}/budget`);
            setData(data);
            if (!data.my_budget) setEditing(true);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not load budget');
        } finally { setLoading(false); }
    }, [tripId]);

    useEffect(() => { load(); }, [load, refreshKey]);

    useEffect(() => { setSelectedCurrency(trip.currency || 'USD'); }, [trip.currency]);

    const save = async (payload) => {
        setSubmitting(true);
        try {
            await api.post(`/trips/${tripId}/budget/me`, payload);
            toast.success('Your budget is saved');
            setEditing(false);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not save');
        } finally { setSubmitting(false); }
    };

    const saveCurrency = async () => {
        setSavingCurrency(true);
        try {
            const { data: r } = await api.post(`/trips/${tripId}/budget/currency`, { currency: selectedCurrency });
            toast.success(`Currency set to ${selectedCurrency}`);
            onTripUpdated?.(r.trip);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not update currency');
        } finally { setSavingCurrency(false); }
    };

    const currency = data?.currency || trip.currency || 'USD';
    const isPremium = !!(data?.is_premium || trip.is_premium);

    const otherBudgets = useMemo(() => (data?.budgets || []).filter((b) => b.user_id !== data?.my_budget?.user_id), [data]);

    if (loading) return <div className="ns-card p-12 text-center text-[var(--ns-text2)] mt-6">Loading budget…</div>;

    return (
        <div data-testid={BUDGET.container} className="mt-6 space-y-6">
            {/* Currency setter (owner only when no budgets submitted yet, or always editable) */}
            <div className="ns-card p-5 sm:p-6">
                <div className="flex items-center gap-3 flex-wrap">
                    <span className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'var(--ns-soft)' }}>
                        <Wallet size={16} className="text-[var(--ns-clay)]" />
                    </span>
                    <div className="flex-1 min-w-[200px]">
                        <div className="font-display font-semibold text-[var(--ns-text)]">Trip currency</div>
                        <div className="text-xs text-[var(--ns-text3)]">Used for every traveler's ceiling and the AI cost breakdown.</div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Select value={selectedCurrency} onValueChange={setSelectedCurrency} disabled={!isOwner}>
                            <SelectTrigger data-testid={BUDGET.currencySelect} className="h-10 w-[120px] rounded-full border-[var(--ns-border)] bg-white">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="rounded-2xl">
                                {(data?.supported_currencies || ['USD']).map((c) => (
                                    <SelectItem key={c} value={c}>{c}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {isOwner && selectedCurrency !== currency && (
                            <Button
                                onClick={saveCurrency}
                                disabled={savingCurrency}
                                data-testid={BUDGET.setCurrencyButton}
                                className="rounded-full h-10 px-4 text-white"
                                style={{ background: 'var(--ns-navy)' }}
                            >
                                {savingCurrency ? 'Saving…' : 'Update'}
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {/* Personal budget form / display */}
            {editing ? (
                <PersonalBudgetForm
                    initial={data?.my_budget}
                    currency={currency}
                    onSave={save}
                    submitting={submitting}
                    onCancel={data?.my_budget ? () => setEditing(false) : undefined}
                />
            ) : (
                <div className="ns-card p-6 sm:p-8">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                            <div className="text-xs uppercase tracking-[0.18em] text-[var(--ns-text3)]">Your ceiling</div>
                            <div className="font-display text-3xl font-semibold tabular-nums mt-1">{fmtMoney(data?.my_budget?.total_ceiling, currency)}</div>
                        </div>
                        <Button
                            variant="outline"
                            onClick={() => setEditing(true)}
                            data-testid={BUDGET.personalEdit}
                            className="rounded-full h-10 px-4 border-[var(--ns-border)]"
                        >
                            <Pencil size={14} className="mr-2" /> Edit
                        </Button>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
                        {CATEGORY_META.map((c) => (
                            <div key={c.key} className="rounded-2xl border border-[var(--ns-border)] p-3 text-center">
                                <div className="text-[10px] uppercase tracking-widest text-[var(--ns-text3)]">{c.label}</div>
                                <div className="text-sm font-semibold tabular-nums mt-1">{fmtMoney(data?.my_budget?.categories?.[c.key], currency)}</div>
                            </div>
                        ))}
                    </div>
                    {data?.my_budget?.notes && (
                        <div className="mt-4 text-sm text-[var(--ns-text2)] italic">"{data.my_budget.notes}"</div>
                    )}
                </div>
            )}

            {/* Alignment overview (FREE) */}
            <div data-testid={BUDGET.alignmentCard} className="ns-card p-6 sm:p-8">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                        <div className="font-display text-lg font-semibold tracking-tight">Group alignment</div>
                        <div className="text-xs text-[var(--ns-text3)]">{data?.submitted}/{data?.total} traveler{data?.total === 1 ? '' : 's'} have entered a budget</div>
                    </div>
                    <StatusChip status={data?.alignment?.status} />
                </div>

                {data?.alignment?.mismatches?.length > 0 && (
                    <div className="mt-5 space-y-2">
                        {data.alignment.mismatches.map((m, i) => (
                            <div key={i} data-testid={BUDGET.mismatchItem} className="rounded-2xl border border-[var(--ns-border)] p-4 flex items-start gap-3" style={{ background: 'rgba(224,122,95,0.06)' }}>
                                <AlertTriangle size={16} className="text-[var(--ns-clay)] mt-0.5 flex-shrink-0" />
                                <div>
                                    <div className="font-semibold text-sm text-[var(--ns-text)]">{m.title}</div>
                                    <div className="text-sm text-[var(--ns-text2)] mt-0.5">{m.message}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Per-participant rows */}
                {(data?.budgets?.length || 0) > 0 && (
                    <div className="mt-5 space-y-2">
                        {data.budgets.map((b) => (
                            <div key={b.user_id} className="flex items-center gap-3">
                                <UserAvatar name={b.user_name} size={28} />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-[var(--ns-text)] truncate">{b.user_name || 'Traveler'}</div>
                                    {b.notes && <div className="text-xs text-[var(--ns-text3)] truncate">{b.notes}</div>}
                                </div>
                                <div className="text-sm font-semibold tabular-nums">{fmtMoney(b.total_ceiling, currency)}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Shared expense splitter (FREE for all participants) */}
            <SharedExpenses trip={trip} refreshKey={refreshKey} />

            {/* AI cost breakdown (PREMIUM) */}
            {isPremium ? (
                data?.breakdown ? (
                    <CostBreakdownCard breakdown={data.breakdown} currency={currency} />
                ) : (
                    <div className="ns-card p-6 text-center text-[var(--ns-text2)]">
                        <RefreshCw size={16} className="inline mr-2 animate-spin" /> Generating destination cost breakdown…
                    </div>
                )
            ) : (
                <PremiumLock
                    title="AI cost breakdown by category"
                    description={`See realistic per-person per-day estimates for ${trip.destination} across stay, food, transport and activities — in your trip currency.`}
                    onUpgrade={onUpgrade}
                    testid={BUDGET.breakdownLocked}
                />
            )}

            {/* Shared cost estimator (PREMIUM) */}
            {isPremium ? (
                data?.shared_estimate && <SharedEstimateCard shared={data.shared_estimate} currency={currency} />
            ) : (
                <PremiumLock
                    title="Shared cost estimator"
                    description="Combine the AI breakdown with your group size to see what a budget / mid-range / luxury trip really costs — per person and total."
                    onUpgrade={onUpgrade}
                    testid={BUDGET.sharedEstimateLocked}
                />
            )}
        </div>
    );
}
